import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatErr } from "../api";

const Field = ({ label, children, span = 1 }) => (
  <div className={span === 2 ? "md:col-span-2" : ""}>
    <label className="overline text-ink-soft block mb-2">{label}</label>
    {children}
  </div>
);

const inputCls =
  "w-full bg-cream border border-ink/15 rounded-sm px-4 py-3 focus:outline-none focus:border-matcha-dark transition-colors text-ink";

export default function Register() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const [form, setForm] = useState({
    full_name: "",
    preferred_name: "",
    email: "",
    password: "",
    date_of_birth: "",
    contact_no: "",
    nric_no: "",
    gender: "Male",
    address: "",
    socso: "",
    epf: "",
    ec_name: "",
    ec_phone: "",
    ec_relationship: "",
  });

  const upd = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.post("/auth/register", {
        email: form.email.trim(),
        password: form.password,
        full_name: form.full_name,
        preferred_name: form.preferred_name,
        date_of_birth: form.date_of_birth,
        contact_no: form.contact_no,
        nric_no: form.nric_no,
        gender: form.gender,
        address: form.address,
        emergency_contact: {
          name: form.ec_name,
          phone: form.ec_phone,
          relationship: form.ec_relationship,
        },
        socso: form.socso,
        epf: form.epf,
      });
      setDone(true);
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-6">
        <div className="max-w-md text-center bg-bone border border-ink/10 rounded-sm p-10">
          <p className="overline text-matcha-dark mb-4">Registered</p>
          <h1 className="font-display text-3xl text-ink mb-3" style={{ letterSpacing: "0.06em" }}>
            You&apos;re on the list.
          </h1>
          <p className="text-ink-soft mb-8 leading-relaxed">
            Your account is awaiting admin approval. You&apos;ll be able to log in
            once your manager activates it.
          </p>
          <button
            data-testid="register-go-login"
            onClick={() => navigate("/staff/login")}
            className="inline-flex items-center gap-2 rounded-full px-7 py-3 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors"
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream px-6 py-16">
      <div className="mx-auto max-w-3xl">
        <Link
          to="/"
          className="font-display text-ink block text-center mb-10"
          style={{ letterSpacing: "0.18em", fontSize: "1.25rem" }}
        >
          MATCHA<span className="text-matcha-dark">·</span>STORY
          <p className="overline text-ink-soft mt-3 tracking-[0.3em]">Staff Registration</p>
        </Link>

        <form
          onSubmit={onSubmit}
          data-testid="register-form"
          className="bg-bone border border-ink/10 rounded-sm p-8 md:p-12 space-y-10"
        >
          {/* Account */}
          <section>
            <h2 className="overline text-matcha-dark mb-5">— Account</h2>
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Email">
                <input data-testid="reg-email" required type="email" className={inputCls} value={form.email} onChange={upd("email")} />
              </Field>
              <Field label="Password (min 6)">
                <input data-testid="reg-password" required type="password" minLength={6} className={inputCls} value={form.password} onChange={upd("password")} />
              </Field>
            </div>
          </section>

          {/* Personal */}
          <section>
            <h2 className="overline text-matcha-dark mb-5">— Personal Details</h2>
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Full name (as per NRIC)">
                <input data-testid="reg-fullname" required className={inputCls} value={form.full_name} onChange={upd("full_name")} />
              </Field>
              <Field label="Preferred name">
                <input data-testid="reg-prefname" className={inputCls} value={form.preferred_name} onChange={upd("preferred_name")} />
              </Field>
              <Field label="Date of birth">
                <input data-testid="reg-dob" required type="date" className={inputCls} value={form.date_of_birth} onChange={upd("date_of_birth")} />
              </Field>
              <Field label="Contact number">
                <input data-testid="reg-contact" required className={inputCls} value={form.contact_no} onChange={upd("contact_no")} placeholder="+60 ..." />
              </Field>
              <Field label="NRIC number">
                <input data-testid="reg-nric" required className={inputCls} value={form.nric_no} onChange={upd("nric_no")} placeholder="000000-00-0000" />
              </Field>
              <Field label="Gender">
                <select data-testid="reg-gender" className={inputCls} value={form.gender} onChange={upd("gender")}>
                  <option>Male</option>
                  <option>Female</option>
                  <option>Other</option>
                </select>
              </Field>
              <Field label="Full address" span={2}>
                <textarea data-testid="reg-address" required rows={2} className={inputCls} value={form.address} onChange={upd("address")} />
              </Field>
            </div>
          </section>

          {/* Statutory */}
          <section>
            <h2 className="overline text-matcha-dark mb-5">— Statutory</h2>
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="SOCSO number">
                <input data-testid="reg-socso" className={inputCls} value={form.socso} onChange={upd("socso")} />
              </Field>
              <Field label="EPF number">
                <input data-testid="reg-epf" className={inputCls} value={form.epf} onChange={upd("epf")} />
              </Field>
            </div>
          </section>

          {/* Emergency contact */}
          <section>
            <h2 className="overline text-matcha-dark mb-5">— Emergency Contact</h2>
            <div className="grid md:grid-cols-3 gap-5">
              <Field label="Name">
                <input data-testid="reg-ec-name" required className={inputCls} value={form.ec_name} onChange={upd("ec_name")} />
              </Field>
              <Field label="Phone">
                <input data-testid="reg-ec-phone" required className={inputCls} value={form.ec_phone} onChange={upd("ec_phone")} />
              </Field>
              <Field label="Relationship">
                <input data-testid="reg-ec-rel" required className={inputCls} value={form.ec_relationship} onChange={upd("ec_relationship")} />
              </Field>
            </div>
          </section>

          {err && (
            <p data-testid="register-error" className="text-sm text-red-700 bg-red-50 px-4 py-3 rounded-sm border border-red-200">
              {err}
            </p>
          )}

          <div className="flex items-center justify-between flex-wrap gap-4 pt-2">
            <Link to="/staff/login" className="text-sm text-ink-soft hover:text-matcha-dark transition-colors">
              ← Back to sign in
            </Link>
            <button
              data-testid="register-submit"
              type="submit"
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-full px-8 py-3 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors disabled:opacity-50"
            >
              {busy ? "Submitting…" : "Submit Registration"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
